import { AssociatedForm } from 'model/Forms';
import { Option, PetType } from '@spike/model';
import { v4 as createUuid } from 'uuid';
import { MasterData } from '@spike/masterdata-model';
import { Marketplace, MarketplaceTax } from '@spike/marketplace-model';
import { Status } from 'model';
import { Room } from '@spike/room-model';
import type { Enrollment, MembershipItem } from '@spike/membership-model';
import { isSettingEnabled } from 'utils/SettingsUtils';

export const SERVICES_BUSINESS_AREAS = {
    GROOMING: 'grooming',
    DENTAL: 'dental',
    DAYCARE: 'daycare',
    BOARDING: 'boarding',
    TRANSPORTATION: 'transportation',
    EVALUATION: 'evaluation',
    TRAINING: 'training',
    WALKING: 'walking'
} as const;

// Business area types for type safety
export type BusinessAreaType = (typeof SERVICES_BUSINESS_AREAS)[keyof typeof SERVICES_BUSINESS_AREAS];

export interface Service {
    id?: number;
    uuid: string;
    siblingUuid?: string;
    marketplaceId?: number;
    name: string;
    description: string;
    duration: ServiceDuration | MetadataDuration;
    pricing: ServicePricing;
    businessArea?: Option<string>;
    petTypes: Array<PetType>;
    staff: Array<StaffMember>;
    status: Option<number>;
    tags: Array<string>;
    color: string | null;
    destroyable: boolean;
    editable: boolean;
    hasReport: boolean;
    houseCall: boolean;
    olbEnabled: boolean;
    forms?: AssociatedForm[];
    // Service-specific metadata (parsed and typed for easy access)
    daycareData?: DaycareMetadata;
    boardingData?: BoardingMetadata;
    transportationData?: TransportationMetadata;
    trainingData?: TrainingMetadata;
    walkingData?: WalkingMetadata;
    // Daycare-specific fields
    dropOffTime?: { start: number; end: number };
    pickUpTime?: { start: number; end: number };
    // Boarding-specific fields
    checkInTime?: { start: number; end: number };
    checkOutTime?: { start: number; end: number };
    // Service items (used for Boarding)
    serviceItems?: ServiceItem[];
    evaluation?: Evaluation;
    marketplaces: Array<{
        marketplaceId: number;
        staffIds: Array<number>;
        taxes: Array<number>;
        roomIds: Array<number>;
        roomUnitIds: Array<number>;
        vehicleIds: Array<number>;
    }>;
}

export interface Meta {
    currentPage: number;
    perPage: number;
    totalServices: number;
    totalPages: number;
}

export interface StaffMember {
    id: number;
    firstName: string;
    lastName: string;
    avatar: string;
}

export interface StaffService {
    uuid: string;
    name: string;
}

export interface ServiceDuration {
    simple?: Duration;
    variable: Array<VariableDuration>;
}

export interface MetadataDuration {
    metadata: string; // JSON string with service specific structure (Daycare, Boarding, etc.)
}

export interface DaycareMetadata {
    durationOption: DaycareDurationOption;
    durationValue: string;
    latePickupFee: DaycareLatePickupFee;
    roomsEnabled: boolean;
    selectedRooms: Array<string>;
}

export const BILLING_CALCULATION = {
    EVERY_24_HOURS: 'every_24_hours',
    CHECK_IN_CHECK_OUT: 'check_in_check_out',
    BY_CALENDAR_DAY: 'by_calendar_day'
} as const;

export type BillingCalculation = (typeof BILLING_CALCULATION)[keyof typeof BILLING_CALCULATION];

const BILLING_CALCULATION_VALUES = new Set<string>(Object.values(BILLING_CALCULATION));

export const isBillingCalculation = (value: unknown): value is BillingCalculation =>
    typeof value === 'string' && BILLING_CALCULATION_VALUES.has(value);

export const resolveBillingCalculation = (billingCalculation?: unknown, dropoff24h?: boolean): BillingCalculation => {
    if (isBillingCalculation(billingCalculation)) {
        return billingCalculation;
    }
    return dropoff24h === true ? BILLING_CALCULATION.EVERY_24_HOURS : BILLING_CALCULATION.CHECK_IN_CHECK_OUT;
};

export const dropoff24hFromBillingCalculation = (billingCalculation: BillingCalculation): boolean =>
    billingCalculation === BILLING_CALCULATION.EVERY_24_HOURS;

/** Same-day By Calendar Day bills 1 day; other modes keep the calendar-night count (0 for same-day). */
export const getBoardingSelectableNights = (
    calendarNights: number,
    billingCalculation?: BillingCalculation,
    quotedNights?: number
): number => {
    if (quotedNights != null && quotedNights > 0) {
        return quotedNights;
    }
    if (billingCalculation === BILLING_CALCULATION.BY_CALENDAR_DAY) {
        return Math.max(1, calendarNights);
    }
    return calendarNights;
};

export interface BoardingMetadata {
    billingCalculation: BillingCalculation;
    checkoutCutoffTime?: number; // HHMM, e.g. 1700 = 5:00 PM
    checkoutGracePeriod?: number; // minutes, By Calendar Day only
    dropoff24h: boolean; // true only for every_24_hours (legacy clients)
    roomsEnabled: boolean;
    selectedRooms: Array<string>;
    minNights: number;
    latePickupFee: BoardingLatePickupFee;
}

export interface BoardingLatePickupFee {
    enabled: boolean;
    type: LateCheckoutFeeType;
    gracePeriod: number;
    fees: Record<string, number>; // petTypeId → amount
}

export interface TransportationMetadata {
    vehiclesEnabled: boolean; // Always true, similar to Boarding roomsEnabled
    selectedVehicles: Array<number>;
}

export const SESSION_BOOKING_TYPES = {
    PRIVATE_SESSION: 'private_session',
    GROUP_SESSION: 'group_session'
} as const;

export type SessionBookingType = (typeof SESSION_BOOKING_TYPES)[keyof typeof SESSION_BOOKING_TYPES];

// Walking and Training services created before booking type existed carry neither field and
// are not being backfilled, so both ends of the API resolve the same defaults. Every read of a
// session-based service config goes through these, so the rule lives in one place.
export const resolveBookingType = (bookingType?: string): SessionBookingType =>
    bookingType === SESSION_BOOKING_TYPES.GROUP_SESSION || bookingType === SESSION_BOOKING_TYPES.PRIVATE_SESSION
        ? bookingType
        : SESSION_BOOKING_TYPES.PRIVATE_SESSION;

export const resolveMaxPets = (bookingType?: string, maxPets?: number): number =>
    resolveBookingType(bookingType) === SESSION_BOOKING_TYPES.GROUP_SESSION ? Math.max(1, maxPets ?? 1) : 1;

// Group or In-Home Walking and Training are scheduled from configured sessions. Private + At
// Facility runs off the staff work schedule and never has one.
export const usesSessions = (bookingType?: string, inHomeService?: boolean): boolean =>
    Boolean(inHomeService) || resolveBookingType(bookingType) === SESSION_BOOKING_TYPES.GROUP_SESSION;

export interface TrainingMetadata {
    hours: number;
    minutes: number;
    bookingType: SessionBookingType;
    maxPets: number;
    inHomeService: boolean;
    apptsCount: number;
}

export interface WalkingMetadata {
    hours: number;
    minutes: number;
    bookingType: SessionBookingType;
    maxPets: number;
    inHomeService: boolean;
}

/**
 * Transportation-specific pricing structure
 * UI shows single pickup/dropoff prices, but backend requires per-pet-type array
 * Same price is applied to all pet types selected in the service
 * Round trip price = pickUpPrice + dropOffPrice (calculated in appointments)
 */
export interface TransportationPricing {
    pickUpPrice: number;
    dropOffPrice: number;
}

export enum ServiceItemType {
    SERVICE = 'Primary Service',
    PRODUCT = 'Product',
    ADD_ON = 'Add-On'
}

// Service Item Types for Boarding
export const SERVICE_ITEM_TYPES = {
    GROOMING_SERVICE: 'Grooming Service',
    DAYCARE_SERVICE: 'Daycare Service',
    ADD_ON: 'Add-On',
    PRODUCT: 'Product'
} as const;

export type BoardingServiceItemType = (typeof SERVICE_ITEM_TYPES)[keyof typeof SERVICE_ITEM_TYPES];

export interface ServiceItem {
    id?: number;
    uuid?: string;
    marketplaceID: number;
    serviceID?: number;
    businessArea?: BusinessAreaType;
    itemID: string;
    itemType: string;
    itemName?: string;
    amount: number;
    quantity: number;
    perStay: boolean;
    duration?: number;
    _destroy?: boolean;
}

export interface DaycareLatePickupFee {
    enabled: boolean;
    type: LatePickupFeeType;
    gracePeriod: number;
    fees: Record<string, number>; // petTypeId → amount
}

export interface Duration {
    hours: number;
    minutes: number;
}

export interface VariableDuration {
    petSize: Option<string>;
    duration: Duration;
}

export interface ServicePricing {
    taxable: boolean;
    taxes: Array<MarketplaceTax>;
    fixedPrice?: Array<FixedPriceOption>;
    variablePrice?: VariablePrice;
}

export interface FixedPriceOption {
    petType: PetType;
    price?: number; // Grooming/Dental/Daycare
    transportationPrice?: TransportationPricing; // Transportation
}

export interface VariablePrice {
    variables: VariablePriceVariables;
    prices: Array<VariablePriceOption>;
}

export interface VariablePriceConstants {
    DOGS: string;
    CATS: string;
    BY_PET_SIZE: string;
    BY_PET_SIZE_AND_HAIR_LENGTH: string;
    BY_HAIR_LENGTH: string;
}

export interface OptionsCache {
    dogs: Option<string>;
    cats: Option<string>;
}

export interface VariablePriceCache {
    petType: string;
    priceType: string;
    variablePrice?: VariablePrice;
}

export interface VariablePriceVariables {
    petTypeVariable: boolean;
    petSizeVariable: boolean;
    hairTypeVariable: boolean;
    hairLengthVariable: boolean;
    exoticTypeVariable: boolean;
}

export interface VariablePriceOption {
    id?: number;
    uuid: string;
    petType?: PetType;
    petSize?: Option<string>;
    hairType?: Option<string>;
    hairLength?: Option<string>;
    exoticType?: Option<string>;
    price?: number;
}

export interface UnifiedPriceObject {
    hairLengths: Array<Option<string>>;
    petSize: Option<string> | undefined;
    petType: PetType | undefined;
    prices: Array<VariablePriceOption>;
}

export interface AddonService {
    name: string;
    price: number;
    status: AddonServiceStatus;
}

export interface DaycareService {
    serviceId: number;
    serviceName: string;
    price: number;
    duration: number;
    serviceColor?: string;
    dropOffTime?: { start: number; end: number };
    pickUpTime?: { start: number; end: number };
    latePickupFee?: LatePickupFee;
    variableDuration?: string;
}

export interface BoardingService {
    serviceId: number;
    serviceName: string;
    price: number;
    /** Original nightly catalog price (before rate_rule / promo offers). */
    catalogPrice?: number;
    duration: number;
    serviceColor?: string;
    checkInTime?: { start: number; end: number };
    checkOutTime?: { start: number; end: number };
    latePickupFee?: LatePickupFee;
    serviceItems?: Array<ServiceItem>;
    rooms?: Array<Room>;
    minimumNights?: number;
    billingCalculation?: BillingCalculation;
    /** Quoted stay nights from boarding_offers (1 for same-day By Calendar Day). */
    quotedNights?: number;
    offers?: Array<BoardingOffer>;
    appliedOffer?: BoardingOffer;
}

export interface BoardingOffer {
    offerType: string;
    id: number;
    uuid: string;
    total: number;
    pricePerNight: number;
    name: string;
    startDate?: string | null;
    endDate?: string | null;
}

export interface TransportationService {
    serviceId: number;
    serviceName: string;
    pickupPrice: number;
    dropoffPrice: number;
    duration: number;
    color: string;
}

export interface TrainingService {
    serviceId: number;
    serviceName: string;
    price: number;
    duration: number;
    color: string;
    bookingType: SessionBookingType;
    maxPets: number;
    inHomeService: boolean;
}

export interface WalkingService {
    serviceId: number;
    serviceName: string;
    price: number;
    duration: number;
    color: string;
    inHomeService: boolean;
    bookingType?: SessionBookingType;
    maxPets?: number;
}

export interface LatePickupFee {
    enabled: boolean;
    type?: string;
    gracePeriod?: number;
    fees?: Fee;
}

export interface Fee {
    dogs?: number;
    cats?: number;
    exotics?: number;
}

export interface DaycareServiceWrapper extends DaycareService {
    durationService?: Duration;
    beforePrice?: number;
    serviceColor?: string;
    latePickupFee?: LatePickupFee;
}

export interface BoardingServiceWrapper extends BoardingService {
    durationService?: Duration;
    beforePrice?: number;
    serviceColor?: string;
    latePickupFee?: LatePickupFee;
    checkInDateTime?: import('moment-timezone').Moment;
    checkOutDateTime?: import('moment-timezone').Moment;
    selectedOffer?: BoardingOffer;
    enrollment?: Enrollment;
    membershipItem?: MembershipItem;
}

export enum AddonServiceStatus {
    ACTIVE = 'active',
    INACTIVE = 'inactive'
}

export enum SortFields {
    NAME = 'name',
    DURATION = 'duration'
}

export enum SortOrder {
    ASCENDANT = 'ascendant',
    DESCENDANT = 'descendant',
    DEFAULT = 'default'
}

// Daycare-specific constants and types
export const DAYCARE_DURATION_OPTIONS = {
    HOURLY: 'hourly',
    HALF_DAY: 'half_day',
    FULL_DAY: 'full_day'
} as const;

export const LATE_PICKUP_FEE_TYPES = {
    PER_HOUR: 'per_hour',
    ADDITIONAL_HALF_DAY: 'additional_half_day',
    ADDITIONAL_FULL_DAY: 'additional_full_day'
} as const;

// Boarding-specific constants and types
export const LATE_CHECKOUT_FEE_TYPES = {
    FIXED_FEE: 'fixed_fee',
    PER_HOUR: 'per_hour',
    ADDITIONAL_NIGHT: 'additional_night'
} as const;

export type DaycareDurationOption = (typeof DAYCARE_DURATION_OPTIONS)[keyof typeof DAYCARE_DURATION_OPTIONS];
export type LatePickupFeeType = (typeof LATE_PICKUP_FEE_TYPES)[keyof typeof LATE_PICKUP_FEE_TYPES];
export type LateCheckoutFeeType = (typeof LATE_CHECKOUT_FEE_TYPES)[keyof typeof LATE_CHECKOUT_FEE_TYPES];

// Type guards for duration types
export const isServiceDuration = (duration: ServiceDuration | MetadataDuration): duration is ServiceDuration => {
    return (
        duration &&
        ((duration as ServiceDuration).simple !== undefined || (duration as ServiceDuration).variable !== undefined)
    );
};

export const isMetadataDuration = (duration: ServiceDuration | MetadataDuration): duration is MetadataDuration => {
    return duration && typeof (duration as MetadataDuration).metadata === 'string';
};

// Type guard for services with Daycare metadata
export const isDaycareService = (service: Service): service is Service & { daycareData: DaycareMetadata } => {
    return service.businessArea?.id === SERVICES_BUSINESS_AREAS.DAYCARE && !!service.daycareData;
};

export const isBoardingService = (service: Service): service is Service & { boardingData: BoardingMetadata } => {
    return service.businessArea?.id === SERVICES_BUSINESS_AREAS.BOARDING && !!service.boardingData;
};

export const isTransportationService = (
    service: Service
): service is Service & { transportationData: TransportationMetadata } => {
    return service.businessArea?.id === SERVICES_BUSINESS_AREAS.TRANSPORTATION && !!service.transportationData;
};

export const isTrainingService = (service: Service): service is Service & { trainingData: TrainingMetadata } => {
    return service.businessArea?.id === SERVICES_BUSINESS_AREAS.TRAINING && !!service.trainingData;
};

// Walking and Training run on sessions and share their appointment behaviour.
export const isSessionBusinessArea = (areaId?: string): boolean =>
    areaId === SERVICES_BUSINESS_AREAS.WALKING || areaId === SERVICES_BUSINESS_AREAS.TRAINING;

export const isWalkingService = (service: Service): service is Service & { walkingData: WalkingMetadata } => {
    return service.businessArea?.id === SERVICES_BUSINESS_AREAS.WALKING && !!service.walkingData;
};

// Helper to create default Daycare metadata
export const getDefaultDaycareMetadata = (): DaycareMetadata => ({
    durationOption: DAYCARE_DEFAULTS.DURATION_OPTION,
    durationValue: DAYCARE_DEFAULTS.DURATION,
    latePickupFee: {
        enabled: false,
        type: LATE_PICKUP_FEE_TYPES.PER_HOUR,
        gracePeriod: DAYCARE_DEFAULTS.GRACE_PERIOD,
        fees: {}
    },
    roomsEnabled: false,
    selectedRooms: []
});

// Helper to create default Boarding metadata
export const getDefaultBoardingMetadata = (): BoardingMetadata => ({
    billingCalculation: BILLING_CALCULATION.CHECK_IN_CHECK_OUT,
    checkoutCutoffTime: BOARDING_DEFAULTS.CHECKOUT_CUTOFF_TIME,
    checkoutGracePeriod: BOARDING_DEFAULTS.CHECKOUT_GRACE_PERIOD,
    dropoff24h: dropoff24hFromBillingCalculation(BILLING_CALCULATION.CHECK_IN_CHECK_OUT),
    roomsEnabled: true,
    selectedRooms: [],
    minNights: BOARDING_DEFAULTS.MIN_NIGHTS,
    latePickupFee: {
        enabled: false,
        type: LATE_CHECKOUT_FEE_TYPES.FIXED_FEE,
        gracePeriod: BOARDING_DEFAULTS.GRACE_PERIOD,
        fees: {}
    }
});

export const buildBoardingRawMetadata = (boardingMetadata: BoardingMetadata) => ({
    billing_calculation: boardingMetadata.billingCalculation,
    checkout_cutoff_time: boardingMetadata.checkoutCutoffTime,
    checkout_grace_period: boardingMetadata.checkoutGracePeriod,
    dropoff_24h: dropoff24hFromBillingCalculation(boardingMetadata.billingCalculation),
    rooms_enabled: boardingMetadata.roomsEnabled,
    selected_rooms: boardingMetadata.selectedRooms,
    min_nights: boardingMetadata.minNights,
    late_pickup_fee: {
        enabled: boardingMetadata.latePickupFee.enabled,
        type: boardingMetadata.latePickupFee.type,
        grace_period: boardingMetadata.latePickupFee.gracePeriod,
        fees: boardingMetadata.latePickupFee.fees
    }
});

// Helper to create default Transportation metadata
export const getDefaultTransportationMetadata = (): TransportationMetadata => ({
    vehiclesEnabled: true,
    selectedVehicles: []
});

// Helper to create default Training metadata
export const getDefaultTrainingMetadata = (): TrainingMetadata => ({
    hours: 1,
    minutes: 0,
    bookingType: SESSION_BOOKING_TYPES.PRIVATE_SESSION,
    maxPets: 1,
    inHomeService: false,
    apptsCount: 0
});

// Helper to create default Walking metadata
export const getDefaultWalkingMetadata = (): WalkingMetadata => ({
    hours: WALKING_DEFAULTS.HOURS,
    minutes: WALKING_DEFAULTS.MINUTES,
    bookingType: WALKING_DEFAULTS.BOOKING_TYPE,
    maxPets: WALKING_DEFAULTS.MAX_PETS,
    inHomeService: WALKING_DEFAULTS.IN_HOME_SERVICE
});

// Daycare defaults
export const DAYCARE_DEFAULTS = {
    DROP_OFF_TIME: { from: '8:00 AM', to: '10:00 AM' },
    PICK_UP_TIME: { from: '1:00 PM', to: '6:00 PM' },
    DURATION_OPTION: DAYCARE_DURATION_OPTIONS.FULL_DAY,
    DURATION: 'Up to 8 hours',
    GRACE_PERIOD: 15,
    HAS_REPORT: false,
    HOUSE_CALL: false,
    ONLINE_BOOKING: false
} as const;

// Boarding defaults
export const BOARDING_DEFAULTS = {
    CHECK_IN_TIME: { from: '2:00 PM', to: '6:00 PM' },
    CHECK_OUT_TIME: { from: '8:00 AM', to: '12:00 PM' },
    CHECKOUT_CUTOFF_TIME: 1700,
    CHECKOUT_CUTOFF_TIME_LABEL: '5:00 PM',
    CHECKOUT_GRACE_PERIOD: 30,
    DROPOFF_24H: false,
    MIN_NIGHTS: 1,
    GRACE_PERIOD: 30,
    HAS_REPORT: true,
    HOUSE_CALL: false,
    ONLINE_BOOKING: false
} as const;

// Transportation defaults
export const TRANSPORTATION_DEFAULTS = {
    VEHICLES_ENABLED: true,
    HAS_REPORT: false,
    HOUSE_CALL: false,
    ONLINE_BOOKING: false
} as const;

// Training defaults
export const TRAINING_DEFAULTS = {
    BOOKING_TYPE: SESSION_BOOKING_TYPES.PRIVATE_SESSION,
    MAX_PETS: 1,
    IN_HOME_SERVICE: false,
    HAS_REPORT: false,
    HOUSE_CALL: false,
    ONLINE_BOOKING: false
} as const;

// Walking defaults
export const WALKING_DEFAULTS = {
    HOURS: 1,
    MINUTES: 0,
    BOOKING_TYPE: SESSION_BOOKING_TYPES.PRIVATE_SESSION,
    MAX_PETS: 1,
    IN_HOME_SERVICE: false,
    HAS_REPORT: false,
    HOUSE_CALL: false,
    ONLINE_BOOKING: false
} as const;

// Factory function for creating empty Daycare services (initialization only)
export const createEmptyDaycareService = (masterData: MasterData, marketplace: Marketplace): Service => {
    const baseService = createEmptyService(masterData, marketplace);
    const daycareMetadata = getDefaultDaycareMetadata();

    // Create raw metadata for backend compatibility
    const rawMetadata = {
        duration_option: daycareMetadata.durationOption,
        duration_value: daycareMetadata.durationValue,
        late_pickup_fee: {
            enabled: daycareMetadata.latePickupFee.enabled,
            type: daycareMetadata.latePickupFee.type,
            grace_period: daycareMetadata.latePickupFee.gracePeriod,
            fees: daycareMetadata.latePickupFee.fees
        },
        rooms_enabled: daycareMetadata.roomsEnabled,
        selected_rooms: daycareMetadata.selectedRooms
    };

    return {
        ...baseService,
        businessArea: masterData.businessAreas.find(ba => ba.id === 'daycare')!,
        // Parsed metadata for easy frontend access
        daycareData: daycareMetadata,
        // Raw metadata for backend compatibility
        duration: {
            metadata: JSON.stringify(rawMetadata)
        } as MetadataDuration,
        // Daycare-specific defaults
        hasReport: DAYCARE_DEFAULTS.HAS_REPORT,
        houseCall: DAYCARE_DEFAULTS.HOUSE_CALL,
        olbEnabled: DAYCARE_DEFAULTS.ONLINE_BOOKING
    };
};

export interface Evaluation {
    id?: number;
    serviceId?: string;
    category: Array<string>;
    evaluationExpiry: EvaluationExpiry;
    evaluationQuestionsEnabled: boolean;
    evaluationQuestions?: Array<EvaluationQuestion>;
}

export interface EvaluationExpiry {
    enabled: boolean;
    duration: string;
}

export interface EvaluationQuestion {
    id?: string | number;
    category: string;
    displayOrder: number;
    enabled: boolean;
    questionText: string;
    questionableId?: string;
    required: boolean;
    questionableType: string;
    marketplaceId?: number;
}

export const createBusinessAreaItem = (value: string) => ({
    id: value.toLowerCase(),
    name: value
});

export const createEmptyEvaluationService = (masterData: MasterData, marketplace: Marketplace): Service => {
    const baseService = createEmptyService(masterData, marketplace);

    return {
        ...baseService,
        businessArea: createBusinessAreaItem('evaluation'),
        evaluation: {
            category: [],
            evaluationExpiry: {
                enabled: false,
                duration: '4'
            } as EvaluationExpiry,
            evaluationQuestionsEnabled: true
        }
    };
};

export const createEmptyService = (masterData: MasterData, marketplace: Marketplace): Service => {
    return {
        uuid: createUuid(),
        marketplaceId: marketplace.id,
        businessArea: marketplace.businessAreas[0],
        name: '',
        description: '',
        duration: { simple: { hours: 0, minutes: 0 }, variable: [] },
        pricing: {
            taxable: false,
            taxes: [],
            fixedPrice: [],
            variablePrice: undefined
        },
        petTypes: [],
        staff: [],
        status: masterData.status.find(s => s.id === Status.ACTIVE)!,
        tags: [],
        color: null,
        destroyable: true,
        editable: true,
        hasReport: true,
        houseCall: isSettingEnabled(marketplace.settings, 'mobile_grooming_enabled'),
        olbEnabled: true,
        forms: [],
        marketplaces: []
    };
};

export const createEmptyBoardingService = (masterData: MasterData, marketplace: Marketplace): Service => {
    const baseService = createEmptyService(masterData, marketplace);
    const boardingMetadata = getDefaultBoardingMetadata();

    // Create raw metadata for backend compatibility
    const rawMetadata = buildBoardingRawMetadata(boardingMetadata);

    return {
        ...baseService,
        businessArea: masterData.businessAreas.find(ba => ba.id === 'boarding')!,
        // Parsed metadata for easy frontend access
        boardingData: boardingMetadata,
        // Raw metadata for backend compatibility
        duration: {
            metadata: JSON.stringify(rawMetadata)
        } as MetadataDuration,
        // Boarding-specific defaults
        hasReport: BOARDING_DEFAULTS.HAS_REPORT,
        houseCall: BOARDING_DEFAULTS.HOUSE_CALL,
        olbEnabled: BOARDING_DEFAULTS.ONLINE_BOOKING
    };
};

export const createEmptyTransportationService = (masterData: MasterData, marketplace: Marketplace): Service => {
    const baseService = createEmptyService(masterData, marketplace);
    const transportationMetadata = getDefaultTransportationMetadata();

    // Create raw metadata for backend compatibility
    const rawMetadata = {
        vehicles_enabled: transportationMetadata.vehiclesEnabled,
        selected_vehicles: transportationMetadata.selectedVehicles
    };

    return {
        ...baseService,
        businessArea: masterData.businessAreas.find(ba => ba.id === 'transportation')!,
        // Parsed metadata for easy frontend access
        transportationData: transportationMetadata,
        // Raw metadata for backend compatibility (NO duration fields)
        duration: {
            metadata: JSON.stringify(rawMetadata)
        } as MetadataDuration,
        // Pricing will be initialized in the pricing step with fixedPrice array
        pricing: {
            ...baseService.pricing,
            fixedPrice: []
        },
        // Transportation-specific defaults
        hasReport: TRANSPORTATION_DEFAULTS.HAS_REPORT,
        houseCall: TRANSPORTATION_DEFAULTS.HOUSE_CALL,
        olbEnabled: TRANSPORTATION_DEFAULTS.ONLINE_BOOKING
    };
};

export const createEmptyTrainingService = (masterData: MasterData, marketplace: Marketplace): Service => {
    const baseService = createEmptyService(masterData, marketplace);
    const trainingMetadata = getDefaultTrainingMetadata();

    // Create raw metadata for backend compatibility (includes duration)
    const rawMetadata = {
        hours: trainingMetadata.hours,
        minutes: trainingMetadata.minutes,
        booking_type: trainingMetadata.bookingType,
        max_pets: trainingMetadata.maxPets,
        in_home_service: trainingMetadata.inHomeService,
        appts_count: trainingMetadata.apptsCount
    };

    // Find training business area, or create it if it doesn't exist
    const trainingBusinessArea = masterData.businessAreas.find(ba => ba.id === 'training') || {
        id: 'training',
        name: 'Training'
    };

    return {
        ...baseService,
        businessArea: trainingBusinessArea,
        // Parsed metadata for easy frontend access
        trainingData: trainingMetadata,
        // Raw metadata for backend compatibility (includes duration)
        duration: {
            metadata: JSON.stringify(rawMetadata)
        } as MetadataDuration,
        // Training-specific defaults
        hasReport: TRAINING_DEFAULTS.HAS_REPORT,
        houseCall: TRAINING_DEFAULTS.HOUSE_CALL,
        olbEnabled: TRAINING_DEFAULTS.ONLINE_BOOKING
    };
};

export const createEmptyWalkingService = (masterData: MasterData, marketplace: Marketplace): Service => {
    const baseService = createEmptyService(masterData, marketplace);
    const walkingMetadata = getDefaultWalkingMetadata();

    // Create raw metadata for backend compatibility (includes duration)
    const rawMetadata = {
        hours: walkingMetadata.hours,
        minutes: walkingMetadata.minutes,
        booking_type: walkingMetadata.bookingType,
        max_pets: walkingMetadata.maxPets,
        in_home_service: walkingMetadata.inHomeService
    };

    // Find walking business area, or create it if it doesn't exist
    const walkingBusinessArea = masterData.businessAreas.find(ba => ba.id === 'walking') || {
        id: 'walking',
        name: 'Walking'
    };

    return {
        ...baseService,
        businessArea: walkingBusinessArea,
        // Parsed metadata for easy frontend access
        walkingData: walkingMetadata,
        // Raw metadata for backend compatibility (includes duration)
        duration: {
            metadata: JSON.stringify(rawMetadata)
        } as MetadataDuration,
        // Walking-specific defaults
        hasReport: WALKING_DEFAULTS.HAS_REPORT,
        houseCall: WALKING_DEFAULTS.HOUSE_CALL,
        olbEnabled: WALKING_DEFAULTS.ONLINE_BOOKING
    };
};

export default Service;
